import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import redirects from "@/data/redirects.json";
import { trackExternalRedirect, buildOutboundUrl } from "@/lib/analytics";
import type { RedirectEntry } from "@/types";

const REDIRECT_MAP = redirects as Record<string, RedirectEntry>;

/**
 * Wait window (D-74): 250ms. Modern gtag.js e fbq usam navigator.sendBeacon
 * internamente; 250ms é flush suficiente sem sacrificar UX. trackExternalRedirect
 * (em analytics.ts) já força transport_type: "beacon" (D-75).
 */
const REDIRECT_WAIT_MS = 250;
const NOT_FOUND_REDIRECT_MS = 1500;

/**
 * Defensive — só permite https. Mesmo que redirects.json seja build-time-only,
 * blindar contra javascript: / data: schemes em caso de PR mal revisada.
 */
function isSafeDestination(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Redirector — rota /r/:slug.
 *
 * Mount lifecycle:
 *   1. Lê slug via useParams; LOWERCASE imediato (D-73 / REDIRECT-10).
 *   2. Injeta <meta name="robots" content="noindex,nofollow"> (D-77 / REDIRECT-06).
 *   3. Lookup em redirects.json:
 *      - Found + safe destination → trackExternalRedirect → wait 250ms → window.location.replace
 *      - Not found OR unsafe → toast + navigate("/") após 1.5s
 *   4. Cleanup: clearTimeout + restore meta robots a "index,follow".
 *
 * Visual: full-screen spinner + caption pt-BR. Background herda gradient body.
 */
const Redirector = () => {
  const params = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Inject noindex,nofollow on mount; restore on unmount (D-77 / REDIRECT-06)
  useEffect(() => {
    let metaTag = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    let createdHere = false;
    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.setAttribute("name", "robots");
      document.head.appendChild(metaTag);
      createdHere = true;
    }
    const previousContent = metaTag.getAttribute("content");
    metaTag.setAttribute("content", "noindex,nofollow");

    return () => {
      if (createdHere) {
        metaTag?.parentNode?.removeChild(metaTag);
      } else if (metaTag) {
        metaTag.setAttribute("content", previousContent ?? "index,follow");
      }
    };
  }, []);

  // Main redirect logic (D-73, D-74, D-75 via trackExternalRedirect, D-76)
  useEffect(() => {
    const rawSlug = params.slug ?? "";
    const slug = rawSlug.toLowerCase(); // D-73 / REDIRECT-10
    const entry = slug ? REDIRECT_MAP[slug] : undefined;

    if (!entry || !isSafeDestination(entry.destination)) {
      toast.error("Link não encontrado 🐒");
      const t = window.setTimeout(() => navigate("/", { replace: true }), NOT_FOUND_REDIRECT_MS);
      return () => window.clearTimeout(t);
    }

    // Found — fire tracking, then 250ms flush window, then replace
    trackExternalRedirect(slug, entry.store);
    const finalUrl = buildOutboundUrl(entry.destination, {
      utm_source: "primata",
      utm_medium: "redirector",
      utm_campaign: `redirect_${slug}`,
    });
    const t = window.setTimeout(() => {
      window.location.replace(finalUrl);
    }, REDIRECT_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [params.slug, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
      <div
        role="status"
        aria-live="polite"
        aria-label="Carregando"
        className="h-12 w-12 animate-spin rounded-full border-4 border-secondary border-t-transparent"
      />
      <p className="text-center text-foreground/80">
        Te levando à melhor oferta... 🐒
      </p>
      {/*
        D-78 / REDIRECT-09: noscript fallback global já está em index.html
        (criado em Plan 1.5 — link hardcoded para WhatsApp). Crawlers e
        usuários com JS desabilitado veem aquele link, não esta página em branco.
      */}
    </div>
  );
};

export default Redirector;
